import { useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loadSizes as mockLoadSizes } from "../../../mocks/calculatorData";
import type { CalcRoom, LoadSize, Selections } from "../../../mocks/calculatorData";

interface SummaryPanelProps {
  rooms: CalcRoom[];
  selections: Selections;
  onClear: () => void;
  onLoadChange?: (loadId: string | null) => void;
  loadSizes?: LoadSize[];
}

function getRecommendedLoad(volume: number): LoadSize | null {
  if (volume <= 0) return null;
  return loadSizes.find((ls) => volume <= ls.maxYards) ?? loadSizes[loadSizes.length - 1];
}

function getFunMessage(qty: number, vol: number): string {
  if (qty === 0) return "Your van is empty... add some rubbish! 👀";
  if (qty === 1) return "Just the one? There's always more hiding somewhere 🤭";
  if (vol < 1) return "Off to a flying start! 🚀";
  if (vol < 3) return "Now we're cooking! 🔥";
  if (vol < 6) return "That's a proper clear-out! 💪";
  if (vol < 10) return "Okay... you've been hoarding a tiny bit 😅";
  if (vol < 14) return "That's basically a whole house! 😱";
  return "MULTIPLE VANS?! Absolute legend. 🏆";
}

function VanGraphic({ fillPct, items }: { fillPct: number; items: Array<{ icon: string; color: string }> }) {
  const capped = Math.min(fillPct, 100);
  const barColor = capped > 85 ? "#ef4444" : capped > 60 ? "#f59e0b" : "#16a34a";
  const glowColor = capped > 85 ? "rgba(239,68,68,0.35)" : capped > 60 ? "rgba(245,158,11,0.35)" : "rgba(22,163,74,0.35)";

  // Van silhouette: diagonal windscreen cut at top-left, cargo box on right
  // polygon: top-right of cab meets cargo at 30%, windscreen angles from (0,30%) to (30%,0%)
  const vanClip = "polygon(30% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 30%)";

  return (
    <div className="w-full select-none">
      <div className="relative" style={{ height: 158 }}>

        {/* ── VAN BODY — clipped to silhouette ── */}
        <div
          className="absolute inset-x-0 top-0"
          style={{ height: 112, clipPath: vanClip }}
        >
          {/* Base body colour */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, #162754 0%, #0d1e3f 100%)" }} />

          {/* Rising fill level */}
          <div
            className="absolute bottom-0 left-0 right-0 transition-all duration-700 ease-out"
            style={{
              height: `${Math.max(capped, 4)}%`,
              background: `linear-gradient(to top, ${barColor}, ${barColor}88)`,
              boxShadow: `0 -6px 24px ${glowColor}`,
            }}
          />

          {/* ─ Cab window (top-left, within the diagonal angle) ─ */}
          <div
            className="absolute"
            style={{
              top: 10, left: 8,
              width: 42, height: 30,
              background: "rgba(132,204,22,0.2)",
              borderRadius: "3px 8px 6px 3px",
              border: "1.5px solid rgba(132,204,22,0.45)",
            }}
          />

          {/* ─ Cargo side window ─ */}
          <div
            className="absolute"
            style={{
              top: 10, left: 56,
              width: 64, height: 24,
              background: "rgba(255,255,255,0.04)",
              borderRadius: 4,
              border: "1px solid rgba(255,255,255,0.07)",
            }}
          />

          {/* ─ Door seam line ─ */}
          <div
            className="absolute top-0 bottom-0"
            style={{ left: "52%", width: 2, background: "rgba(255,255,255,0.05)" }}
          />

          {/* ─ CLEARIO badge ─ */}
          <div className="absolute" style={{ top: 11, right: 12 }}>
            <span
              className="text-[8px] font-black tracking-[0.18em] px-2 py-0.5 rounded-full"
              style={{ background: "#84cc16", color: "#fff" }}
            >
              CLEARIO
            </span>
          </div>

          {/* ─ Item icons float in cargo area ─ */}
          {items.length > 0 && (
            <div className="absolute bottom-2 left-3 right-3 flex flex-wrap gap-1 z-10">
              {items.slice(0, 14).map((it, idx) => (
                <span
                  key={idx}
                  className="w-6 h-6 flex items-center justify-center rounded"
                  style={{ background: it.color + "cc", border: "1px solid rgba(255,255,255,0.18)" }}
                >
                  <i className={`${it.icon} text-white`} style={{ fontSize: 9 }}></i>
                </span>
              ))}
            </div>
          )}

          {/* ─ % readout ─ */}
          <div
            className="absolute font-black text-white tabular-nums z-10"
            style={{
              top: "52%", right: "22%",
              transform: "translateY(-50%)",
              fontSize: 26,
              textShadow: "0 2px 10px rgba(0,0,0,0.8)",
              lineHeight: 1,
            }}
          >
            {Math.round(capped)}
            <span style={{ fontSize: 11 }}>%</span>
          </div>
        </div>

        {/* ── CHASSIS STRIP ── */}
        <div
          className="absolute left-0 right-0"
          style={{ top: 108, height: 10, background: "#0f172a", borderRadius: "0 0 4px 4px" }}
        />

        {/* ── FRONT BUMPER ── */}
        <div
          className="absolute"
          style={{
            top: 104, left: 2,
            width: 14, height: 14,
            background: "#1e293b",
            borderRadius: "0 0 4px 0",
            border: "1.5px solid #0f172a",
          }}
        />

        {/* ── REAR BUMPER ── */}
        <div
          className="absolute"
          style={{
            top: 104, right: 2,
            width: 14, height: 14,
            background: "#1e293b",
            borderRadius: "0 0 0 4px",
            border: "1.5px solid #0f172a",
          }}
        />

        {/* ── WHEELS ── */}
        <div
          className="absolute flex justify-between"
          style={{ bottom: 4, left: "11%", right: "9%" }}
        >
          {[0, 1].map((w) => (
            <div
              key={w}
              className="relative flex items-center justify-center rounded-full"
              style={{ width: 38, height: 38, background: "#0f172a" }}
            >
              {/* Tyre */}
              <div
                className="absolute inset-0 rounded-full"
                style={{ background: "#1a1a2e", border: "4px solid #0f172a" }}
              />
              {/* Rim */}
              <div
                className="relative z-10 flex items-center justify-center rounded-full"
                style={{ width: 20, height: 20, background: "#c8d4e0", border: "2px solid #94a3b8" }}
              >
                {/* Hub */}
                <div
                  className="rounded-full"
                  style={{ width: 8, height: 8, background: "#84cc16", border: "1.5px solid #4d7c0f" }}
                />
              </div>
              {/* Rim spokes x3 */}
              {[0, 60, 120].map((deg) => (
                <div
                  key={deg}
                  className="absolute rounded-full"
                  style={{
                    width: 2,
                    height: 14,
                    background: "#94a3b8",
                    top: "50%",
                    left: "50%",
                    transform: `translate(-50%, -50%) rotate(${deg}deg)`,
                  }}
                />
              ))}
            </div>
          ))}
        </div>

        {/* ── GROUND SHADOW ── */}
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none"
          style={{
            width: "88%",
            height: 8,
            background: "radial-gradient(ellipse at center, rgba(0,0,0,0.2) 0%, transparent 70%)",
            filter: "blur(4px)",
          }}
        />
      </div>
    </div>
  );
}

export default function SummaryPanel({ rooms, selections, onClear, onLoadChange, loadSizes = mockLoadSizes }: SummaryPanelProps) {
  const navigate = useNavigate();
  const prevLoadId = useRef<string | null>(null);

  const allItems = useMemo(() => rooms.flatMap((r) => r.items), [rooms]);

  const roomColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    rooms.forEach((r) => r.items.forEach((item) => { map[item.id] = r.accentColor; }));
    return map;
  }, [rooms]);

  const selectedItems = useMemo(() => {
    return allItems
      .filter((item) => (selections[item.id] ?? 0) > 0)
      .map((item) => ({
        ...item,
        qty: selections[item.id] ?? 0,
        totalVol: (selections[item.id] ?? 0) * item.volume,
        color: roomColorMap[item.id] ?? "#6CC412",
      }));
  }, [allItems, selections, roomColorMap]);

  const totalVolume = useMemo(() => selectedItems.reduce((s, i) => s + i.totalVol, 0), [selectedItems]);
  const totalQty = useMemo(() => selectedItems.reduce((s, i) => s + i.qty, 0), [selectedItems]);

  const recommended = getRecommendedLoad(totalVolume);
  const overMax = totalVolume > 18;
  // Fill % is relative to the full van capacity (18 yd³) so it grows linearly as items are added
  const fillPct = totalVolume > 0 ? Math.min((totalVolume / 18) * 100, 100) : 0;

  // Notify parent on load change
  useEffect(() => {
    const id = recommended?.id ?? null;
    if (id !== prevLoadId.current) {
      if (onLoadChange && prevLoadId.current !== null) onLoadChange(id);
      prevLoadId.current = id;
    }
  }, [recommended, onLoadChange]);

  const vanItems = selectedItems.flatMap((it) =>
    Array.from({ length: Math.min(it.qty, 4) }, () => ({ icon: it.icon, color: it.color }))
  );

  return (
    <div className="rounded-3xl overflow-hidden thick-border cartoon-shadow">
      {/* Header */}
      <div className="px-6 py-5 flex items-center justify-between" style={{ background: "#09245b" }}>
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-cl-lime thick-border flex-shrink-0">
            <i className="ri-truck-line text-cl-dark text-lg"></i>
          </span>
          <div>
            <p className="text-white font-black text-base leading-tight">Your Van</p>
            <p className="text-white/50 text-xs font-bold mt-0.5">
              {totalQty > 0
                ? `${totalQty} item${totalQty !== 1 ? "s" : ""} · ${totalVolume.toFixed(1)} yd³`
                : "Add items to get started"}
            </p>
          </div>
        </div>
        {totalQty > 0 && (
          <button
            onClick={onClear}
            className="flex items-center gap-1 text-white/40 hover:text-white/80 text-xs font-black transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-delete-bin-line text-sm"></i>
            Clear
          </button>
        )}
      </div>

      <div className="bg-white px-5 py-5 flex flex-col gap-5">
        {/* Van graphic */}
        <VanGraphic fillPct={fillPct} items={vanItems} />

        {/* Fun message */}
        <div
          className="rounded-2xl px-4 py-3 text-center thick-border"
          style={{ background: "#f8f8f8" }}
        >
          <p className="text-sm font-black text-cl-dark leading-snug">
            {getFunMessage(totalQty, totalVolume)}
          </p>
        </div>

        {/* Empty state hint */}
        {totalQty === 0 && (
          <div className="py-4 text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              {["ri-sofa-line", "ri-hotel-bed-line", "ri-fridge-line"].map((ic) => (
                <span key={ic} className="w-10 h-10 flex items-center justify-center rounded-xl bg-cl-lime thick-border">
                  <i className={`${ic} text-cl-dark text-sm`}></i>
                </span>
              ))}
            </div>
            <p className="text-cl-dark/40 text-xs font-bold">Tap any item on the left to add it</p>
          </div>
        )}

        {/* Recommendation */}
        {recommended && !overMax && (
          <div
            className="rounded-2xl p-4 thick-border anim-slide-up"
            style={{ background: "#6CC412" }}
          >
            <p className="text-[10px] font-black text-cl-dark/50 uppercase tracking-widest mb-3">
              Recommended for you
            </p>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-cl-dark thick-border flex-shrink-0">
                <i className={`${recommended.icon} text-cl-lime text-2xl`}></i>
              </span>
              <div>
                <p className="font-black text-cl-dark text-base leading-tight">{recommended.label}</p>
                <p className="text-xs text-cl-dark/60 font-semibold leading-snug mt-0.5">
                  {recommended.description}
                </p>
              </div>
              {recommended.popular && (
                <span className="ml-auto px-2.5 py-1 rounded-full text-[10px] font-black text-cl-dark bg-white thick-border whitespace-nowrap">
                  ⭐ Top pick
                </span>
              )}
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-[10px] font-bold text-cl-dark/50 uppercase tracking-wide">Est. price</p>
                <p className="font-black text-cl-dark leading-none anim-price-pop" style={{ fontSize: 28 }}>
                  £{recommended.price.toFixed(2)}
                  <span className="text-xs font-bold text-cl-dark/40 ml-1">inc. VAT</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold text-cl-dark/50">Up to</p>
                <p className="text-sm font-black text-cl-dark">
                  {recommended.maxYards} yd³ capacity
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Over max */}
        {overMax && (
          <div className="rounded-2xl p-4 thick-border" style={{ background: "#FEF3C7" }}>
            <p className="font-black text-cl-dark text-sm mb-1">🏆 That&apos;s a LOT of stuff!</p>
            <p className="text-xs text-cl-dark/60 font-semibold leading-relaxed">
              Your total ({totalVolume.toFixed(1)} yd³) needs multiple van runs. Call us and we&apos;ll sort a tailored quote — no drama.
            </p>
            <a
              href="tel:03335775445"
              className="mt-3 inline-flex items-center gap-2 font-black text-cl-dark text-xs hover:text-cl-green transition-colors cursor-pointer"
            >
              <i className="ri-phone-fill"></i>03335 775445
            </a>
          </div>
        )}

        {/* Items list */}
        {selectedItems.length > 0 && (
          <div>
            <p className="text-[10px] font-black text-cl-dark/40 uppercase tracking-widest mb-2">
              In the van
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedItems.map((item) => (
                <span
                  key={item.id}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-black"
                  style={{ background: item.color + "22", border: `1.5px solid ${item.color}40`, color: "#09245b" }}
                >
                  <i className={`${item.icon} text-xs`} style={{ color: item.color }}></i>
                  {item.label}
                  {item.qty > 1 && (
                    <span
                      className="w-4 h-4 flex items-center justify-center rounded-full text-white text-[9px] font-black"
                      style={{ background: item.color }}
                    >
                      {item.qty}
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        {recommended && !overMax && (
          <button
            onClick={() => navigate("/book")}
            className="w-full py-4 rounded-2xl font-black text-white text-base transition-all cursor-pointer whitespace-nowrap anim-wiggle"
            style={{
              background: "#6CC412",
              border: "2.5px solid #0f172a",
              boxShadow: "4px 4px 0 #0f172a",
            }}
            onMouseOver={(e) => { e.currentTarget.style.transform = "translate(2px,2px)"; e.currentTarget.style.boxShadow = "2px 2px 0 #0f172a"; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "4px 4px 0 #0f172a"; }}
            onMouseDown={(e) => { e.currentTarget.style.transform = "translate(4px,4px)"; e.currentTarget.style.boxShadow = "0px 0px 0 #0f172a"; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = "translate(2px,2px)"; e.currentTarget.style.boxShadow = "2px 2px 0 #0f172a"; }}
          >
            <i className="ri-calendar-check-line mr-2"></i>
            Book {recommended.label} — £{recommended.price.toFixed(2)}
          </button>
        )}

        {totalQty > 0 && (
          <p className="text-[10px] text-cl-dark/35 font-semibold text-center leading-relaxed">
            Estimate only · Final price confirmed at booking · Special item surcharges may apply
          </p>
        )}
      </div>
    </div>
  );
}
